import type { CSSProperties } from "react";
import { motion } from "motion/react";
import type { PlayerView } from "../api/types";
import type { Theme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { uiText } from "../theme/styles";
import { ManaIcon } from "./ManaIcon";
import { Sigil } from "./Sigil";
import {
  deckKey,
  exileKey,
  graveKey,
  useBoardRefs,
  type BoardRefKey,
} from "../logic/boardRefs";
import { zoneMoveTransition } from "../logic/motion";

interface Props {
  view: PlayerView;
  name: string;
  faction: string;
  /** Defaults to "{faction} · {hue}". */
  subtitle?: string;
  side: "you" | "opponent";
  theme: Theme;
  targetable?: boolean;
  targetPicked?: boolean;
  onTargetClick?: () => void;
  /** Click during target mode when this seat isn't a candidate (plays
   *  the notice sfx without accepting the pick). */
  onInvalidTargetClick?: () => void;
  /** Deck is wired only on the viewer's own panel (opponent's deck is
   *  private); grave / exile are public on both seats. */
  onOpenDeck?: () => void;
  onOpenGraveyard?: () => void;
  onOpenExile?: () => void;
  deckHighlighted?: boolean;
  graveyardHighlighted?: boolean;
  exileHighlighted?: boolean;
  /** Total life this player has lost this turn — drives the "−N" marker
   *  overlayed on the panel, matching the damage marker on creatures. */
  lifeLossThisTurn?: number;
}

export function PlayerPanel({
  view,
  name,
  faction,
  subtitle,
  side,
  theme,
  targetable,
  targetPicked,
  onTargetClick,
  onInvalidTargetClick,
  onOpenDeck,
  onOpenGraveyard,
  onOpenExile,
  deckHighlighted,
  graveyardHighlighted,
  exileHighlighted,
  lifeLossThisTurn,
}: Props) {
  const f = factionFor(faction);
  const isYou = side === "you";
  const borderColor = targetPicked
    ? theme.accent
    : targetable
      ? E32.amber
      : theme.panelEdge;
  const panel: CSSProperties = {
    background: theme.panel,
    color: theme.ink,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: 8,
    position: "relative",
    overflow: "hidden",
    minHeight: 0,
    flex: 1,
    border: `1px solid ${borderColor}`,
    boxShadow: targetable ? `0 0 0 1px ${borderColor}40` : undefined,
    cursor: targetable ? "pointer" : "default",
  };
  const maxHp = 100;

  const panelClickable =
    (targetable && onTargetClick) || onInvalidTargetClick;
  const handlePanelClick = targetable && onTargetClick
    ? onTargetClick
    : onInvalidTargetClick
      ? onInvalidTargetClick
      : undefined;
  return (
    <div
      style={panel}
      onClick={handlePanelClick}
      role={panelClickable ? "button" : undefined}
      tabIndex={panelClickable ? 0 : undefined}
      aria-label={
        panelClickable
          ? `${name} — ${targetable ? "valid target" : "target"}`
          : undefined
      }
      onKeyDown={
        panelClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePanelClick?.();
              }
            }
          : undefined
      }
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: f.color,
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 40,
            height: 40,
            background: f.color,
            border: `1.5px solid ${f.color}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Sigil faction={f.key} size={28} color={f.ink} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: theme.ink,
            }}
          >
            {name}
          </div>
          <div style={uiText(theme, { size: 13, dim: true })}>
            {subtitle ?? `${f.name} · ${f.hue}`}
          </div>
        </div>
      </div>

      <div
        style={{
          background: theme.bgDeep,
          border: `1px solid ${theme.panelEdge}`,
          borderRadius: theme.radius.button,
          padding: "8px 10px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={uiText(theme, { size: 12, dim: true })}>Life</div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 22,
            fontWeight: 700,
            color: view.life <= 25 ? E32.rust : theme.ink,
            lineHeight: 1,
            position: "relative",
          }}
        >
          {view.life}
          <span style={{ fontSize: 12, color: theme.inkFaint, marginLeft: 4 }}>/{maxHp}</span>
          {lifeLossThisTurn != null && lifeLossThisTurn > 0 && (
            <div
              // Same visual contract as the per-creature "−N" damage
              // marker rendered by Field.tsx: rust pill, dark border.
              // Anchored to the top-right (above the /maxHp suffix) so
              // it doesn't sit on top of the current-life digits.
              style={{
                position: "absolute",
                top: -8,
                right: -4,
                padding: "1px 6px",
                background: E32.rust,
                color: E32.white,
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                border: `2px solid ${theme.bgDeep}`,
              }}
            >
              −{lifeLossThisTurn}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          background: theme.bgDeep,
          border: `1px solid ${theme.panelEdge}`,
          borderRadius: theme.radius.button,
          padding: "8px 10px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            ...uiText(theme, { size: 12, dim: true }),
            marginBottom: 8,
          }}
        >
          <span>Mana</span>
          <span style={{ fontFamily: theme.fontDisplay, fontSize: 14, color: theme.inkDim }}>
            {view.mana}/{view.max_mana}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 4,
            justifyItems: "center",
          }}
        >
          {Array.from({ length: 10 }).map((_, i) => (
            <ManaIcon
              key={i}
              filled={i < view.mana}
              exists={i < view.max_mana}
              theme={theme}
              faction={f}
            />
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <StackIndicator
          label="Deck"
          count={view.deck_size}
          theme={theme}
          onClick={onOpenDeck}
          highlighted={deckHighlighted}
          // Board-ref anchor for FLIP draws/mills out of this deck.
          anchorKey={deckKey(view.player_id)}
          ghostEids={view.deck?.map((c) => c.entity_id)}
        />
        <StackIndicator
          label="Hand"
          count={isYou ? (view.hand?.length ?? view.hand_size) : view.hand_size}
          theme={theme}
        />
        <StackIndicator
          label="Grave"
          count={view.graveyard_size}
          theme={theme}
          onClick={onOpenGraveyard}
          highlighted={graveyardHighlighted}
          anchorKey={graveKey(view.player_id)}
          ghostEids={view.graveyard?.map((c) => c.entity_id)}
        />
        <StackIndicator
          label="Exile"
          count={view.exile_size}
          theme={theme}
          onClick={onOpenExile}
          highlighted={exileHighlighted}
          anchorKey={exileKey(view.player_id)}
          ghostEids={view.exile?.map((c) => c.entity_id)}
        />
      </div>
    </div>
  );
}

function StackIndicator({
  label,
  count,
  theme,
  onClick,
  highlighted,
  anchorKey,
  ghostEids,
}: {
  label: string;
  count: number;
  theme: Theme;
  onClick?: () => void;
  highlighted?: boolean;
  /** Register this pill as a board-refs anchor so cards crossing this
   *  zone land on its rect. Omitted for the Hand pill. */
  anchorKey?: BoardRefKey;
  /** Invisible per-eid placeholders stacked at the pill's rect, each
   *  carrying Motion's `layoutId: card-<eid>` so the pill acts as the
   *  source / destination rect for that eid's shared-layout transition. */
  ghostEids?: ReadonlyArray<number>;
}) {
  const boardRefs = useBoardRefs();
  const interactive = onClick !== undefined;
  const borderColor = highlighted ? theme.accent : theme.panelEdge;
  return (
    <div
      ref={anchorKey ? (el) => boardRefs.register(anchorKey, el) : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label}: ${count}` : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={{
        background: theme.bgDeep,
        border: `1px solid ${borderColor}`,
        boxShadow: highlighted ? `0 0 0 1px ${theme.accent}` : undefined,
        borderRadius: theme.radius.button,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        cursor: interactive ? "pointer" : "default",
        userSelect: "none",
        position: "relative",
      }}
    >
      <div style={uiText(theme, { size: 12, dim: true })}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline" }}>
        <span
          style={{ fontFamily: theme.fontDisplay, fontSize: 16, fontWeight: 700, color: theme.ink }}
        >
          {count}
        </span>
      </div>
      {ghostEids?.map((eid) => (
        <motion.span
          key={eid}
          layoutId={`card-${eid}`}
          transition={zoneMoveTransition()}
          aria-hidden="true"
          style={{
            // 1px ghost pinned at the pill's geometric centre — gives
            // Motion's `layoutId` an unambiguous source/dest rect;
            // stretched-rect ghosts land off-centre when the parent
            // clips their outer pixels.
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
}
